import React, { useState, useMemo, useRef, useCallback } from 'react';
import scheduleData from '../../data/fullSchedule.json'
import * as htmlToImage from 'html-to-image';

const AISymposiumSchedule = ({ scheduleData, eventName = "AI Uni Conference" }) => {
  const [currentPage, setCurrentPage] = useState(0);

  const A4Page = ({ dayData, pageNumber, columns }) => (
    <div 
      className="bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900 mx-auto my-4 shadow-2xl border border-cyan-500/10 relative overflow-hidden"
      style={{
        width: '794px',
        minHeight: '1123px',
      }}
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #06b6d4 2px, transparent 2px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 p-12 h-full flex flex-col">
        {/* Elegant Header */}
        <div className="text-center mb-8 pb-6 border-b border-cyan-500/20 flex-shrink-0">
          <div className="inline-block">
            <div className="w-16 h-1 bg-gradient-to-r from-cyan-400 to-emerald-400 mx-auto mb-3 rounded-full"></div>
            <h1 className="text-4xl font-light text-cyan-100 mb-2 tracking-wide">{eventName}</h1>
            <div className="w-16 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 mx-auto mt-3 rounded-full"></div>
          </div>
          <h2 className="text-xl font-light text-cyan-300/80 mt-4">{dayData.date}</h2>
          <div className="text-sm text-cyan-400/50 mt-1 font-mono">Page {pageNumber}</div>
        </div>

        {/* Two Column Content */}
        <div className="flex-1 grid grid-cols-2 gap-8">
          {columns.map((column, colIndex) => (
            <div key={colIndex} className="relative">
              {/* Vertical timeline line for each column */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400/20 via-cyan-400/40 to-cyan-400/20"></div>
              
              <div className="space-y-6 ml-8">
                {column.map((item, index) => (
                  <ScheduleItem key={`${colIndex}-${index}`} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 mt-6 pt-4 border-t border-cyan-500/10 text-center">
          <p className="text-cyan-400/40 text-xs font-light tracking-wide">AI UNI CONFERENCE 2025 • DELHI</p>
        </div>
      </div>
    </div>
  );

  const ScheduleItem = ({ item }) => {
    const isHighlight = item.id || (item.title && (
      item.title.includes('Plenary') || 
      item.title.includes('Technical') || 
      item.title.includes('Panel') ||
      item.title.includes('Session')
    ));
    
    const isMinor = item.title && (
      item.title.includes('Registration') ||
      item.title.includes('Lunch') ||
      item.title.includes('Coffee') ||
      item.title.includes('Break') ||
      item.title.includes('Inauguration') ||
      item.title.includes('Vote of Thanks') ||
      item.title.includes('Felicitation')
    );

    if (isMinor) {
      return (
        <div className="flex items-start group">
          {/* Timeline dot */}
          <div className="relative z-10 flex-shrink-0 w-8 h-8 flex items-center justify-center -mt-1">
            <div className="w-2 h-2 bg-cyan-400/20 rounded-full group-hover:bg-cyan-400/40 transition-all duration-300"></div>
          </div>
          
          {/* Content */}
          <div className="flex-1 ml-4">
            <div className="bg-slate-800/20 backdrop-blur-sm rounded-lg p-3 border border-cyan-500/5 hover:border-cyan-500/10 transition-all duration-300">
              <div className="text-center">
                <span className="text-cyan-400/60 text-xs font-mono tracking-wide bg-cyan-400/5 px-2 py-1 rounded-full">
                  {item.time}
                </span>
                <h3 className="text-slate-400 text-xs mt-1 font-light tracking-wide leading-tight">{item.title}</h3>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-start group">
        {/* Timeline dot */}
        <div className="relative z-10 flex-shrink-0 w-8 h-8 flex items-center justify-center -mt-1">
          <div className={`w-3 h-3 rounded-full ${
            isHighlight 
              ? 'bg-gradient-to-r from-cyan-400 to-emerald-400 shadow-lg shadow-cyan-400/20' 
              : 'bg-cyan-400/40'
          } group-hover:scale-125 transition-transform duration-300`}></div>
        </div>
        
        {/* Content */}
        <div className="flex-1 ml-4">
          <div className={`bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-sm rounded-xl p-4 border transition-all duration-300 hover:scale-[1.02] ${
            isHighlight 
              ? 'border-cyan-500/20 shadow-lg shadow-cyan-500/10 hover:shadow-cyan-500/20' 
              : 'border-cyan-500/10 shadow-md shadow-blue-500/5'
          }`}>
            
            {/* Time and Title */}
            <div className="mb-4">
              <span className={`font-mono text-xs px-3 py-1 rounded-full ${
                isHighlight 
                  ? 'bg-cyan-400/10 text-cyan-300 border border-cyan-400/20' 
                  : 'bg-slate-700/30 text-slate-300'
              }`}>
                {item.time}
              </span>
              <h3 className={`mt-2 leading-tight ${
                isHighlight 
                  ? 'text-lg font-light bg-gradient-to-r from-cyan-100 to-emerald-100 bg-clip-text text-transparent' 
                  : 'text-base text-slate-200 font-light'
              }`}>
                {item.title}
              </h3>
            </div>

            {/* Venue and Theme */}
            {(item.venue || item.theme) && (
              <div className="mb-3 space-y-2">
                {item.venue && (
                  <p className="text-cyan-300/80 text-xs flex items-center">
                    <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-2"></span>
                    {item.venue}
                  </p>
                )}
                {item.theme && (
                  <p className="text-emerald-300/80 text-xs flex items-center">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></span>
                    {item.theme}
                  </p>
                )}
              </div>
            )}

            {/* People */}
            {item.people && item.people.length > 0 && (
              <div className="mb-3">
                <div className="space-y-1">
                  {item.people.map((person, idx) => (
                    <div key={idx} className="flex items-center text-xs">
                      <span className="text-cyan-400/60 font-light min-w-12 tracking-wide">{person.type}:</span>
                      <span className="text-slate-300 ml-2 font-light leading-tight">{person.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Session Details */}
            {item.sessionDetails && (
              <div className="mt-3">
                <div className="space-y-2">
                  {item.sessionDetails.map((session, idx) => (
                    <div key={idx} className="bg-slate-800/30 rounded-lg p-2 border border-cyan-500/10">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-cyan-300 font-mono text-xs bg-cyan-400/10 px-2 py-0.5 rounded">
                          {session.time}
                        </span>
                        {session.index && (
                          <span className="text-xs bg-emerald-400/10 text-emerald-300 px-2 py-0.5 rounded font-light">
                            {session.index}
                          </span>
                        )}
                      </div>
                      
                      {session.speaker && (
                        <p className="text-slate-200 text-xs font-light mb-1 leading-tight">
                          {session.speaker}
                        </p>
                      )}
                      
                      {session.topic && (
                        <p className="text-cyan-200/70 text-xs font-light italic leading-tight">
                          {session.topic}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Calculate pagination and column distribution
  const { pages, totalPages } = useMemo(() => {
    const pages = [];
    
    scheduleData.forEach(dayData => {
      const dayPages = [];
      let currentColumn = [[], []]; // [leftColumn, rightColumn]
      let currentHeight = 0;
      const maxHeight = 800; // Approximate max height for one column

      const calculateItemHeight = (item) => {
        let height = 80; // Base height for minor items
        
        if (item.id || item.title?.includes('Plenary') || item.title?.includes('Session')) {
          height = 120; // Base height for major items
          
          // Add height for additional content
          if (item.venue || item.theme) height += 30;
          if (item.people) height += item.people.length * 20;
          if (item.sessionDetails) height += item.sessionDetails.length * 40;
          if (item.sessionDetails?.some(s => s.speaker && s.topic)) height += 30;
        }
        
        return height;
      };

      dayData.schedule.forEach((item, index) => {
        const itemHeight = calculateItemHeight(item);
        const currentColIndex = currentHeight > maxHeight ? 1 : 0;
        
        // If adding this item would exceed column height and we're still in first column
        if (currentColIndex === 0 && currentHeight + itemHeight > maxHeight) {
          // Move to second column
          currentColumn[1].push(item);
          currentHeight = itemHeight;
        } else if (currentColIndex === 1 && currentHeight + itemHeight > maxHeight) {
          // Both columns full, create new page
          dayPages.push([...currentColumn]);
          currentColumn = [[item], []];
          currentHeight = itemHeight;
        } else {
          // Add to current column
          currentColumn[currentColIndex].push(item);
          currentHeight += itemHeight;
        }
      });

      // Add the last page if there are remaining items
      if (currentColumn[0].length > 0 || currentColumn[1].length > 0) {
        dayPages.push([...currentColumn]);
      }

      pages.push(...dayPages.map(columns => ({ dayData: { ...dayData, schedule: columns.flat() }, columns })));
    });

    return { pages, totalPages: pages.length };
  }, [scheduleData]);

  const currentPageData = pages[currentPage] || { dayData: scheduleData[0], columns: [[], []] };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-blue-900/10 to-slate-900 min-h-screen py-8">
      {/* Navigation and Info */}
      <div className="no-print p-6 text-center mb-8">
        <div className="inline-block bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 p-px rounded-2xl mb-4">
          <div className="bg-slate-900/80 backdrop-blur-sm px-8 py-3 rounded-2xl">
            <p className="text-cyan-300/80 font-light tracking-wide text-sm">
              AI CONFERENCE SCHEDULE • TWO-COLUMN LAYOUT • PAGE {currentPage + 1} OF {totalPages}
            </p>
          </div>
        </div>
        
        {/* Navigation Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="px-4 py-2 bg-cyan-500/10 text-cyan-300 rounded-lg disabled:opacity-30 hover:bg-cyan-500/20 transition-all"
            >
              Previous Page
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-4 py-2 bg-emerald-500/10 text-emerald-300 rounded-lg disabled:opacity-30 hover:bg-emerald-500/20 transition-all"
            >
              Next Page
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col items-center">
        <A4Page 
          dayData={currentPageData.dayData} 
          pageNumber={currentPage + 1} 
          columns={currentPageData.columns}
        />
      </div>
      
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%) !important;
          }
          .no-print {
            display: none;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
};

// Usage component
const AISchedule = () => {
  // Your JSON data would be imported here

  return (
    <AISymposiumSchedule 
      scheduleData={scheduleData} 
      eventName="AI Uni Conference" 
    />
  );
};

export default AISchedule;